/*****************************************************************************/
/*  File Name: freeTipSelect.js                                              */
/*  Purpose:   Handles manually-entered free tip allocations for ROI reports  */
/*****************************************************************************/

import React from 'react';
import { withTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import styles from './styles/roi.module.scss';
import { MenuItem, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { useROIContext } from '../../context/roiContext';
import { convertToCurrency } from '../../functions/convertCurrency';
import { useTipContext } from '../../context/tipContext';
import { convertToNumber } from '../../functions/convertNumber';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SoltaSelect from './elements/SelectField';
import TipPriceTextField from './elements/TipPriceField';

function FreeTipSelect({ t, systemId, consumerMode = false }) {
  const {
    freeTips,
    setFreeTips,
    addAnotherFreeTip,
    removeFreeTip,
    addFreeTipCount,
    removeFreeTipCount,
  } = useROIContext();

  const { tips, tipsLoading } = useTipContext();
  const availableTips = systemId
    ? tips.filter((tip) => tip.system === systemId || tip.techID === systemId)
    : tips;
  const isAvailableTip = (tipId) => availableTips.some((tip) => tip.id === tipId);

  return (
    <Grid item xs={12} container className={`${styles.tipSelectGrid} ${styles.freeTipSelectGrid} ${styles.mobileTreatmentCards}`}>
      <Grid item xs={12} container className={styles.tipHeadline} justifyContent='space-between'>
        <Grid item className={styles.title}>
          Free Tips
        </Grid>
        <Grid item>
          <Button className={styles.addTreatmentButton} onClick={() => addAnotherFreeTip()} startIcon={<AddCircleOutlineIcon />}>
            Add free tip
          </Button>
        </Grid>
      </Grid>

      <Grid item xs={12} container alignItems='flex-end' className={`${styles.tipLine} ${styles.headline}`}>
        <Grid item className={styles.removeGrid}>
          {/* Remove button place holder*/}
        </Grid>
        <Grid item xs className={styles.tipSelectDropDown}>
          Treatment Tip
        </Grid>
        <Grid item xs className={styles.quantityGrid}>
          Free Tips
        </Grid>
        <Grid item xs className={`${styles.tipPriceGrid} `}>
          Revenue Per Treatment
        </Grid>
      </Grid>
      <Grid item xs={12} container className={styles.tipGroup}>
        {!tipsLoading
          ? freeTips.map((myTip, i) => (
            <Grid item xs={12} container key={i} alignItems='center' className={styles.tipLine}>
              <Grid item className={styles.removeGrid}>
                <Tooltip title='Remove Item?'>
                  <IconButton onClick={() => removeFreeTip(i)} className={styles.removeButton}>
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs className={styles.tipSelectDropDown} data-label='Treatment tip'>
                <SoltaSelect
                  labelId='free-tip-select'
                  id='free-tip-select'
                  value={isAvailableTip(freeTips[i].id) ? freeTips[i].id ?? '' : ''}
                  onChange={(event) =>
                    setFreeTips((prevFreeTips) => {
                      const updatedFreeTips = prevFreeTips.map((tip) => ({ ...tip }));
                      const selectedTip = tips.find((tip) => tip.id === event.target.value);

                      if (selectedTip) {
                        const currentFreeTipCount = isAvailableTip(prevFreeTips[i].id)
                          ? convertToNumber(prevFreeTips[i].freeTipCount ?? 0)
                          : 0;

                        updatedFreeTips[i] = {
                          ...selectedTip,
                          freeTipCount: currentFreeTipCount > 0 ? currentFreeTipCount : 1,
                          revenuePerTreatment: convertToNumber(selectedTip.treatmentPrice ?? selectedTip.suggestedTXcost ?? 0),
                        };
                      }

                      return updatedFreeTips;
                    })
                  }
                >
                  {availableTips.map((tip) => (
                    <MenuItem value={tip.id} key={tip.id}>
                      <Typography className={styles.tipName}>{tip.sku}</Typography>
                    </MenuItem>
                  ))}
                </SoltaSelect>
              </Grid>
              <Grid item xs container alignItems='center' justifyContent='center' className={styles.quantityGrid} data-label='Free tips'>
                <Grid item xs className={styles.quantityButtons} alignSelf='center'>
                  <Tooltip title='Subtract Quantity'>
                    <span>
                      <IconButton disabled={!isAvailableTip(myTip.id) || convertToNumber(myTip.freeTipCount ?? 0) === 0} className={styles.quantityButton} onClick={() => removeFreeTipCount(i)}>
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
                <Grid item xs className={styles.quantityField} alignSelf='center'>
                  <TipPriceTextField
                    disabled={!isAvailableTip(myTip.id)}
                    value={isAvailableTip(myTip.id) ? convertToNumber(myTip.freeTipCount ?? 0) : 0}
                    onChange={(event) =>
                      setFreeTips((prevFreeTips) => {
                        const updatedFreeTips = prevFreeTips.map((tip) => ({ ...tip }));
                        updatedFreeTips[i].freeTipCount = convertToNumber(event.target.value);
                        return updatedFreeTips;
                      })
                    }
                  />
                </Grid>
                <Grid item xs className={styles.quantityButtons}>
                  <Tooltip title='Add Quantity'>
                    <span>
                      <IconButton disabled={!myTip.id || !isAvailableTip(myTip.id)} className={styles.quantityButton} onClick={() => addFreeTipCount(i)}>
                        <AddCircleOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
              <Grid item xs className={`${styles.tipPriceGrid} ${styles.price}`} data-label='Revenue per treatment'>
                <TipPriceTextField
                  disabled={!isAvailableTip(myTip.id)}
                  value={isAvailableTip(myTip.id) ? convertToCurrency(myTip.revenuePerTreatment) ?? 0 : 0}
                  onChange={(event) =>
                    setFreeTips((prevFreeTips) => {
                      const updatedFreeTips = prevFreeTips.map((tip) => ({ ...tip }));
                      updatedFreeTips[i].revenuePerTreatment = convertToNumber(event.target.value);
                      return updatedFreeTips;
                    })
                  }
                />
              </Grid>
            </Grid>
          ))
          : 'Please select a machine'}
      </Grid>
    </Grid>
  );
}

export default withTranslation()(FreeTipSelect);
